import React, { useState, useEffect, useMemo } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  ReferenceLine, // <-- 1. Import ReferenceLine
} from 'recharts';
import { getPlayerCumulativeWinnings } from '../../utils/parseData';
import '../../styles/components/_cumulativeWinnings.css';

// Utility function copied from GamesTable.js to parse the Date(YYYY,MM,DD) format
const formatDate = (dateString) => {
  // Regex to match the Date(year,month,day) format
  const match = dateString.match(/Date\((\d+),(\d+),(\d+)\)/);
  if (!match) return dateString; // Return original string if format doesn't match
  
  // month from Sheets/JSON is 0-indexed (0=Jan, 11=Dec)
  const [_, year, month, day] = match; 
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  
  // Use the parsed month (which is 0-indexed) and day/year
  return `${monthNames[parseInt(month)]} ${parseInt(day)}, ${year}`;
};


// CustomTooltip (no changes)
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const gameNumber = payload[0].payload.gameNumber;
    const date = payload[0].payload.date;

    return (
      <div className="custom-tooltip">
        <p className="tooltip-label">{`Game ${gameNumber} (${date})`}</p>
        {payload.map((p) => (
          <p className="tooltip-entry" key={p.dataKey} style={{ color: p.color }}>
            {p.dataKey}: ${p.value.toFixed(2)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

/**
 * A component to display cumulative winnings for multiple players.
 */
const CumulativeWinnings = ({ selectedPlayers, colors }) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (selectedPlayers.length === 0) {
      setData([]);
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const promises = selectedPlayers.map((name) =>
          getPlayerCumulativeWinnings(name)
        );
        const results = await Promise.all(promises);

        const allGameNumbers = new Set();
        results.forEach((playerData) => {
          playerData.forEach((game) => allGameNumbers.add(game.gameNumber));
        });

        const sortedGameNumbers = [...allGameNumbers].sort((a, b) => a - b);

        const combinedData = sortedGameNumbers.map((gameNum) => {
          const dataPoint = { gameNumber: gameNum };
          let date = 'Unknown';

          results.forEach((playerData, index) => {
            const playerName = selectedPlayers[index];
            const gameData = playerData.find(
              (g) => g.gameNumber === gameNum
            );
            
            if (gameData) {
              dataPoint[playerName] = gameData.cumulativeWinnings;
              date = formatDate(gameData.date);
            } else {
              const lastGame = playerData.filter(g => g.gameNumber < gameNum).pop();
              dataPoint[playerName] = lastGame ? lastGame.cumulativeWinnings : 0;
            }
          });
          dataPoint.date = date;
          return dataPoint;
        });

        setData(combinedData);
      } catch (err) {
        setError('Failed to fetch player winnings data.');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [selectedPlayers]);

  const chartTicks = useMemo(() => {
    if (data.length === 0) return [];
    const maxGame = Math.max(...data.map(d => d.gameNumber));
    const ticks = [1];
    for (let i = 5; i <= maxGame; i += 5) {
      ticks.push(i);
    }
    return ticks;
  }, [data]);

  const noPlayersSelected = selectedPlayers.length === 0;
  
  const defaultXDomain = [1, 20];
  const defaultXTicks = [1, 5, 10, 15, 20];
  const defaultYDomain = [-100, 100];

  if (isLoading) {
    return (
      <div className="cumulative-winnings-container empty-state">
        <p>Loading chart...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cumulative-winnings-container empty-state">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="cumulative-winnings-container">
      <div className="chart-wrapper">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{ top: 5, right: 20, left: -10, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />

            {/* 2. Add the ReferenceLine here */}
            <ReferenceLine 
              y={0} 
              stroke="var(--text-color)" // Use your theme's text color
              strokeWidth={2}
              strokeOpacity={0.75} // Make it bold but not overpowering
            />

            <XAxis
              dataKey="gameNumber"
              tick={{ fontSize: 12 }}
              type="number"
              domain={noPlayersSelected ? defaultXDomain : ['dataMin', 'dataMax']}
              ticks={noPlayersSelected ? defaultXTicks : chartTicks}
            />
            <YAxis
              tickFormatter={(tick) => `$${tick}`}
              tick={{ fontSize: 12 }}
              domain={noPlayersSelected ? defaultYDomain : undefined}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            
            {selectedPlayers.map((name) => (
              <Line
                key={name}
                type="monotone"
                dataKey={name}
                stroke={colors[name] || '#8884d8'}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 6 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default CumulativeWinnings;